/**
 * json-render component definitions and rules. Consumed via `pnpm generate:component-catalog`
 * (backend/S3), not from the chat client.
 */

import { defineCatalog } from "@json-render/core";
import { schema } from "@json-render/react/schema";
import { shadcnComponentDefinitions } from "@json-render/shadcn/catalog";
import { ChatAreaChartSpec } from "@/components/ui/area-chart/ChatAreaChartSpec";
import {
  ChatBarChartSpec,
  ChatStackedBarChartSpec,
} from "@/components/ui/bar-chart/ChatBarChartSpec";
import { ChatButtonSpec } from "@/components/ui/button/ChatButtonSpec";
import { ChatBlockquoteSpec } from "@/components/ui/chat/blockquote/ChatBlockquoteSpec";
import { ChatMarkdownSpec } from "@/components/ui/chat/ChatMarkdownSpec";
import { ChatCalloutSpec } from "@/components/ui/chat/callout/ChatCalloutSpec";
import { ChatDataListSpec } from "@/components/ui/chat/data-list/ChatDataListSpec";
import { ChatHorizontalBarSpec } from "@/components/ui/chat/horizontal-bar/ChatHorizontalBarSpec";
import { ChatImageSpec } from "@/components/ui/chat/image/ChatImageSpec";
import { ChatLinkSpec } from "@/components/ui/chat/link/ChatLinkSpec";
import {
  ChatMetricGroupSpec,
  ChatMetricSpec,
} from "@/components/ui/chat/metric/ChatMetricSpec";
import { ChatSuggestionChipsSpec } from "@/components/ui/chat/suggestion-chips/ChatSuggestionChipsSpec";
import { ChatTableSpec } from "@/components/ui/chat/table/ChatTableSpec";
import { ChatTimelineSpec } from "@/components/ui/chat/timeline/ChatTimelineSpec";
import { ChatComposedChartSpec } from "@/components/ui/composed-chart/ChatComposedChartSpec";
import { ChatLineChartSpec } from "@/components/ui/line-chart/ChatLineChartSpec";
import { ChatPieDonutChartSpec } from "@/components/ui/pie-chart/ChatPieDonutChartSpec";
import { ChatRadarChartSpec } from "@/components/ui/radar-chart/ChatRadarChartSpec";
import { ChatRadialBarChartSpec } from "@/components/ui/radial-bar-chart/ChatRadialBarChartSpec";
import { ChatSparkChartRowSpec } from "@/components/ui/spark-chart/ChatSparkChartRowSpec";

const HERALD_EXPLORER_SHADCN_COMPONENT_KEY_LIST = [
  "Stack",
  "Grid",
  "Skeleton",
  "Progress",
  "Badge",
  "Card",
] as const satisfies readonly (keyof typeof shadcnComponentDefinitions)[];

/**
 * Names in {@link explorerCatalog} whose `props` come from `shadcnComponentDefinitions`
 * (not Chat* specs). `satisfies` ensures each key exists on the shadcn definitions object.
 */
export const HERALD_EXPLORER_SHADCN_COMPONENT_KEYS = new Set<string>(
  HERALD_EXPLORER_SHADCN_COMPONENT_KEY_LIST,
);

/**
 * Layout and composition — first in the prompt so specs stay intentional
 * regardless of user prompt wording.
 */
const CATALOG_LAYOUT_RULES = [
  `LAYOUT & COMPOSITION:
**Root**: Stack (direction: 'vertical', gap: 'md') — never Card as root.

**Default spine** (adapt titles/content to the prompt; keep order):
1. Context — Card (maxWidth: 'full', title + description) or Callout (info)
2. KPIs — MetricGroup (2–4 items, direct Stack child; not inside Card/Grid)
3. Evidence — one or more Table sections (full width; title per table). Use Table for articles, citations, Q&A, narratives — not bullet lists.
4. Charts — **2+ charts**: Grid (columns: 2, gap: 'md') → one Card (maxWidth: 'full') per cell → one chart each (trend + composition pairs). **1 chart**: single Card (maxWidth: 'full'). Set height ~200–280 so pairs align.
5. Insights — Callout(s) after data/charts (warning = risks/response; info = summary; recommended_action = next steps)
6. Follow-ups — SuggestionChips last

**Constraints**: Card — one section, never nested; children = text, Metric, DataList, Table, or one chart (never MetricGroup). Grid — charts side-by-side or 3 equal peer Cards; never dense narrative Cards (Quote + bars + Callout — stack vertically). Hyperlink articles/sources in Table columns and via Link — not raw URLs.`,
];

/**
 * Behavioral rules not covered by layout or component descriptions.
 */
const CATALOG_GENERATIVE_UI_RULES = [
  `SUGGESTION CHIPS:
End every data/analysis spec with SuggestionChips (2–4 items). Each chip: short imperative label (3–6 words) + full self-contained prompt sent on click. Tailor to the analysis — never generic. Do not use Callout or plain text for follow-ups.`,

  `MARKDOWN:
Use Markdown only for short headings, introductions, and explanatory body copy when structured components do not fit. Do not use Markdown for article lists, citations, comparisons, metrics, warnings, recommendations, or follow-up actions; use Table, Metric or MetricGroup, Callout, and SuggestionChips respectively. Keep Markdown within the Stack spine or its relevant Card; never replace the structured layout with a Markdown blob.`,

  `WORKFLOW SHORTCUTS:
Component choices for common tasks (follow the layout spine):
- **Story deep-dive**: Card → Quote + DataList + Metric + RadialBarChart + Table (Link)
- **Article detail**: Card (Image, title, Quote, DataList, HorizontalBar, Link) — use Card title for headline; no standalone Heading
- **Trending / dual-axis**: ComposedChart (bars = volume, line = impact) — full-width Card when alone
- **Period comparison**: MetricGroup with variant 'delta' (previousValue, delta, deltaPercent, trend) + AreaChart or SparkChartRow
- **X / social — analyze_x_narrative**: MetricGroup + Card (Quote summary) + DataList + Callout (warning for mutation risk — never Badge for sentences)
- **X / social — get_top_x_posts**: Table (author, excerpt, likes, reposts, views, verified Badge, Link)
- **X / social — get_narrative_influencers**: Table (handle, followers, stance Badge, influence type, tactic) + Callout if coordination flagged
- **X / social — combined report**: MetricGroup at top, then Cards stacked vertically (one story each — not Grid), then Tables + Callout`,
];

/**
 * Chart, data-binding, and style reference rules.
 */
const CATALOG_CUSTOM_RULES = [
  `CHART SELECTION:
- **BarChart**: Single series (xKey + yKey). Per-publisher or single distribution.
- **StackedBarChart**: Multi-series stacked (xKey + series[]). Sentiment over time.
- **ComposedChart**: Mixed bar + line + area on same xKey.
- **LineChart** / **AreaChart**: Trends; prefer AreaChart for sentiment magnitude.
- **PieChart** / **RadialBarChart**: Proportions, SOV, gauges (nameKey + valueKey).
- **RadarChart**: Multi-axis compare (dataKey or dataKeys[] for feeds).
- **SparkChartRow**: 2–4 KPI sparklines sharing one time index.
- **HorizontalBar**: Compact sentiment strip (showLabels + showValues; Positive → Neutral → Negative).
- **Time grain**: ≤31d daily · 32–89d weekly · ≥90d monthly.`,

  `CHART COLORS & SENTIMENT:
- Default: omit color props. Labels/keys containing positive/negative/neutral auto-color; other series cycle var(--chart-1)–var(--chart-5). Order segments Positive → Neutral → Negative.
- Set a color only when the user asks for a specific one, and only as #RGB or #RRGGBB — never var(), rgb(), or a named color.
- Where it goes: AreaChart / LineChart / RadarChart (single series) → color. StackedBarChart / ComposedChart → series[].color. RadarChart (multi-series) → dataKeys[].color. HorizontalBar → segments[].color. BarChart / PieChart / RadialBarChart → colors as { "<key>": "#hex" }.
- Colors are per-entry: anything you omit keeps its sentiment or palette default.`,

  `SPEC INTEGRITY & DATA BINDING:
- Every child id in a children array must exist in elements.
- Put fetched data in /state; reference with { "$state": "/path" }. Emit /state patches before elements that use them.`,

  `TONE & STYLE:
No emojis. Clean, scannable, information-dense. Highlight negative sentiment and rising narratives. Always link articles (Link component or [text](url) in prose — never raw URLs).`,
];

/** All rules combined (layout first, then generative UI, then reference). Used where a single array is needed. */
export const CATALOG_RULES = [
  ...CATALOG_LAYOUT_RULES,
  ...CATALOG_GENERATIVE_UI_RULES,
  ...CATALOG_CUSTOM_RULES,
];

export const explorerCatalog = defineCatalog(schema, {
  components: {
    // ── @json-render/shadcn (shadcnComponentDefinitions + default examples) ──
    Stack: {
      structureDescription: "Group of blocks.",
      props: shadcnComponentDefinitions.Stack.props,
      description:
        "Required spec root (direction: 'vertical', gap: 'md'). Children: context Card, MetricGroup, Table(s), Grid chart row or chart Card, Callout(s), SuggestionChips.",
      example: { direction: "vertical", gap: "md" },
    },
    Grid: {
      structureDescription: "Blocks arranged side by side.",
      props: shadcnComponentDefinitions.Grid.props,
      description:
        "Side-by-side row inside Stack. Primary use: columns: 2, gap: 'md' with one Card + one chart per cell. Also columns: 3 for three equal peer Cards.",
      example: { columns: 2, gap: "md" },
    },
    Skeleton: {
      props: shadcnComponentDefinitions.Skeleton.props,
      description:
        "Loading placeholder. Use when content is being fetched or not yet available (e.g. table or chart loading). Optional width, height, rounded.",
      example: { width: "100%", height: "2rem", rounded: true },
    },
    Progress: {
      structureDescription: "Progress bar.",
      props: shadcnComponentDefinitions.Progress.props,
      description: `${shadcnComponentDefinitions.Progress.description}. Use for a single metric's completion or usage. For multiple labeled segments that sum to a whole (e.g. sentiment mix), use HorizontalBar, PieChart, or StackedBarChart instead.`,
      example: shadcnComponentDefinitions.Progress.example,
    },
    Badge: {
      structureDescription: "Short status label.",
      props: shadcnComponentDefinitions.Badge.props,
      description: `${shadcnComponentDefinitions.Badge.description}. Compact status or sentiment label (≤ 5 words). Required text. Optional variant: 'default' | 'secondary' | 'destructive' | 'outline'. Sentiment: positive = default, negative = destructive, neutral = secondary. NEVER put sentences or explanatory text in a Badge — use Callout instead.`,
      example: { text: "Neutral", variant: "secondary" },
    },
    Card: {
      structureDescription: "Titled card with a chart, table, or text.",
      props: shadcnComponentDefinitions.Card.props,
      description:
        "One section; maxWidth: 'full' for primary blocks. Children: text, Metric, DataList, Table, or one chart — never MetricGroup. Never root or nested.",
      example: {
        title: "Key takeaways",
        description: "Summary for the selected period.",
        maxWidth: "full",
        centered: false,
      },
    },
    Image: {
      structureDescription: "Image or logo.",
      props: ChatImageSpec.props,
      description: ChatImageSpec.description,
      example: ChatImageSpec.example,
    },

    // ── Custom app components (Chat* specs; implementations in HeraldSpecRenderer registry) ──
    BarChart: {
      structureDescription: "Bar chart comparing values across categories.",
      props: ChatBarChartSpec.props,
      description: ChatBarChartSpec.description,
      example: ChatBarChartSpec.example,
    },
    StackedBarChart: {
      structureDescription: "Stacked bar chart comparing categories.",
      props: ChatStackedBarChartSpec.props,
      description: ChatStackedBarChartSpec.description,
      example: ChatStackedBarChartSpec.example,
    },
    ComposedChart: {
      structureDescription: "Combined bar and line chart.",
      props: ChatComposedChartSpec.props,
      description: ChatComposedChartSpec.description,
      example: ChatComposedChartSpec.example,
    },
    AreaChart: {
      structureDescription: "Area chart showing a trend over time.",
      props: ChatAreaChartSpec.props,
      description: ChatAreaChartSpec.description,
      example: ChatAreaChartSpec.example,
    },
    LineChart: {
      structureDescription: "Line chart showing a trend over time.",
      props: ChatLineChartSpec.props,
      description: ChatLineChartSpec.description,
      example: ChatLineChartSpec.example,
    },
    HorizontalBar: {
      structureDescription: "Horizontal bar showing how parts make up a whole.",
      props: ChatHorizontalBarSpec.props,
      description: ChatHorizontalBarSpec.description,
      example: ChatHorizontalBarSpec.example,
    },
    PieChart: {
      structureDescription: "Pie chart showing a breakdown.",
      props: ChatPieDonutChartSpec.props,
      description: ChatPieDonutChartSpec.description,
      example: ChatPieDonutChartSpec.example,
    },
    RadarChart: {
      structureDescription: "Radar chart comparing several measures.",
      props: ChatRadarChartSpec.props,
      description: ChatRadarChartSpec.description,
      example: ChatRadarChartSpec.example,
    },
    RadialBarChart: {
      structureDescription: "Radial chart showing a breakdown.",
      props: ChatRadialBarChartSpec.props,
      description: ChatRadialBarChartSpec.description,
      example: ChatRadialBarChartSpec.example,
    },
    SparkChartRow: {
      props: ChatSparkChartRowSpec.props,
      description: ChatSparkChartRowSpec.description,
      example: ChatSparkChartRowSpec.example,
    },
    Metric: {
      structureDescription: "Single key metric.",
      props: ChatMetricSpec.props,
      description: ChatMetricSpec.description,
      example: ChatMetricSpec.example,
    },
    MetricGroup: {
      structureDescription: "Row of key metrics.",
      props: ChatMetricGroupSpec.props,
      description: ChatMetricGroupSpec.description,
      example: ChatMetricGroupSpec.example,
    },
    Table: {
      structureDescription: "Table of rows and columns.",
      props: ChatTableSpec.props,
      description: ChatTableSpec.description,
      example: ChatTableSpec.example,
    },
    Button: {
      props: ChatButtonSpec.props,
      description: ChatButtonSpec.description,
      example: ChatButtonSpec.example,
    },
    Link: {
      structureDescription: "Link to a web page.",
      props: ChatLinkSpec.props,
      description: `${ChatLinkSpec.description} Use for original articles and source URLs — never use Button for URLs.`,
      example: ChatLinkSpec.example,
    },
    Callout: {
      props: ChatCalloutSpec.props,
      description: ChatCalloutSpec.description,
      example: ChatCalloutSpec.example,
    },
    DataList: {
      structureDescription: "List of labeled values.",
      props: ChatDataListSpec.props,
      description: `${ChatDataListSpec.description} Prefer DataList over ad-hoc text or tables for simple key-value metadata.`,
      example: ChatDataListSpec.example,
    },
    Quote: {
      props: ChatBlockquoteSpec.props,
      description: `${ChatBlockquoteSpec.description} Props: content (required), attribution (speaker/name), source (publication). Use to surface the most impactful excerpt when showing article or story details.`,
      example: ChatBlockquoteSpec.example,
    },
    Markdown: {
      structureDescription: "Formatted text.",
      props: ChatMarkdownSpec.props,
      description: ChatMarkdownSpec.description,
      example: ChatMarkdownSpec.example,
    },
    SuggestionChips: {
      props: ChatSuggestionChipsSpec.props,
      description: ChatSuggestionChipsSpec.description,
      example: ChatSuggestionChipsSpec.example,
    },
    Timeline: {
      props: ChatTimelineSpec.props,
      description: ChatTimelineSpec.description,
      example: ChatTimelineSpec.example,
    },
  },
  actions: {},
});
